import { AlertCircle, Check, Clipboard, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppNotification } from '../../types/notification';
import { cn } from '../../lib/utils';

interface Props {
  notification: AppNotification;
  onClose: () => void;
}

interface SectionProps {
  label: string;
  value: string | number | undefined | null;
  mono?: boolean;
  multiline?: boolean;
}

const Field: React.FC<SectionProps> = ({ label, value, mono = false, multiline = false }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      {multiline ? (
        <pre
          className={cn(
            'rounded-md bg-muted/60 border border-border/60 p-3 text-xs leading-relaxed overflow-x-auto whitespace-pre-wrap break-all text-foreground max-h-56 overflow-y-auto',
            mono && 'font-mono',
          )}
        >
          {String(value)}
        </pre>
      ) : (
        <p className={cn('text-sm text-foreground break-all', mono && 'font-mono text-xs bg-muted/40 rounded px-2 py-0.5 inline-block')}>
          {String(value)}
        </p>
      )}
    </div>
  );
};

export const SystemErrorDetailModal: React.FC<Props> = ({ notification, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const d = notification.systemErrorDetails;

  // The clipboard report stays in English on purpose: it is a technical
  // diagnostic meant to be pasted into bug reports for the dev team.
  const copyAll = () => {
    const lines: string[] = [
      `=== System Error Report ===`,
      `Timestamp : ${notification.createdAt}`,
      `Title     : ${notification.title ?? '—'}`,
      `Message   : ${notification.description ?? '—'}`,
      '',
    ];

    if (d) {
      if (d.errorType) lines.push(`Error Type   : ${d.errorType}`);
      if (d.httpStatus) lines.push(`HTTP Status  : ${d.httpStatus}`);
      if (d.httpMethod) lines.push(`HTTP Method  : ${d.httpMethod}`);
      if (d.endpoint) lines.push(`Endpoint     : ${d.endpoint}`);
      if (d.source) lines.push(`Source File  : ${d.source}`);
      if (d.lineno != null) lines.push(`Line / Col   : ${d.lineno}:${d.colno ?? '?'}`);
      if (d.url) lines.push(`Page URL     : ${d.url}`);
      if (d.userAgent) lines.push(`User Agent   : ${d.userAgent}`);
      if (d.responseSnippet) lines.push(`\n--- Response Body ---\n${d.responseSnippet}`);
      if (d.extra) {
        lines.push('\n--- Extra Context ---');
        for (const [k, v] of Object.entries(d.extra)) lines.push(`${k}: ${v}`);
      }
      if (d.stack) lines.push(`\n--- Stack Trace ---\n${d.stack}`);
    }

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    /* Full-screen backdrop */
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center animate-fade-in"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal panel */}
      <div
        className="relative w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col glass-card rounded-xl shadow-2xl border-destructive/30 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60 flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-foreground truncate">
              {notification.title ?? t('notifications.errorDetail.systemError')}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {new Date(notification.createdAt).toLocaleString()}
            </p>
          </div>

          {/* Copy all button */}
          <button
            onClick={copyAll}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-all duration-150',
              copied
                ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted hover:text-foreground',
            )}
            title={t('notifications.errorDetail.copyAllTitle')}
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" />
                {t('notifications.errorDetail.copied', 'Copied!')}
              </>
            ) : (
              <>
                <Clipboard className="h-3.5 w-3.5" />
                {t('notifications.errorDetail.copy', 'Copy all')}
              </>
            )}
          </button>

          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            aria-label={t('global.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {/* Error message */}
          <Field
            label={t('notifications.errorDetail.message', 'Error Message')}
            value={notification.description}
          />

          {/* HTTP context */}
          {(d?.httpStatus || d?.endpoint) && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-destructive/70">
                {t('notifications.errorDetail.network', 'Network / API')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {d?.httpStatus && (
                  <Field label={t('notifications.errorDetail.httpStatus', 'HTTP Status')} value={d.httpStatus} mono />
                )}
                {d?.httpMethod && (
                  <Field label={t('notifications.errorDetail.httpMethod', 'Method')} value={d.httpMethod} mono />
                )}
              </div>
              <Field label={t('notifications.errorDetail.endpoint', 'Endpoint')} value={d?.endpoint} mono />
              {d?.responseSnippet && (
                <Field
                  label={t('notifications.errorDetail.responseBody', 'Response Body')}
                  value={d.responseSnippet}
                  mono
                  multiline
                />
              )}
            </div>
          )}

          {/* JS error context */}
          {(d?.errorType || d?.source || d?.lineno != null) && (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={t('notifications.errorDetail.errorType', 'Error Type')}
                value={d?.errorType}
                mono
              />
              {d?.lineno != null && (
                <Field
                  label={t('notifications.errorDetail.location', 'Location')}
                  value={`${d.lineno}:${d.colno ?? '?'}`}
                  mono
                />
              )}
              <div className="col-span-2">
                <Field
                  label={t('notifications.errorDetail.sourceFile', 'Source File')}
                  value={d?.source}
                  mono
                />
              </div>
            </div>
          )}

          {/* Stack trace */}
          {d?.stack && (
            <Field
              label={t('notifications.errorDetail.stackTrace', 'Stack Trace')}
              value={d.stack}
              mono
              multiline
            />
          )}

          {/* Browser / page context */}
          <div className="space-y-3 border-t border-border/40 pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('notifications.errorDetail.context', 'Context')}
            </p>
            <Field
              label={t('notifications.errorDetail.pageUrl', 'Page URL')}
              value={d?.url}
              mono
            />
            <Field
              label={t('notifications.errorDetail.userAgent', 'User Agent')}
              value={d?.userAgent}
              mono
            />
          </div>

          {/* Extra key/value pairs */}
          {d?.extra && Object.keys(d.extra).length > 0 && (
            <div className="space-y-2 border-t border-border/40 pt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('notifications.errorDetail.extra', 'Extra Context')}
              </p>
              {Object.entries(d.extra).map(([k, v]) => (
                <Field key={k} label={k} value={v} mono />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemErrorDetailModal;
